import { ExternalLink } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

export default function Footer() {
  const { t } = useLanguage();

  const quickLinks = [
    { key: "home", href: "#home" },
    { key: "about", href: "#about" },
    { key: "accommodations", href: "#accommodations" },
    { key: "location", href: "#location" },
    { key: "gallery", href: "#gallery" },
    { key: "contact", href: "#contact" },
  ];

  return (
    <footer className="bg-[#1F2937] text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-5">
              {/* Logo substituído pela imagem do cliente */}
              <div className="w-10 h-10 overflow-hidden rounded-lg bg-white p-1">
                <img 
                  src="/cb1ce97b-ee70-4a21-a6cd-ee93462e9112.jpg" 
                  alt="Logo Glamp Your Holiday" 
                  className="w-full h-full object-contain"
                />
              </div>
              <span className="font-playfair font-semibold text-lg tracking-tight">
                Glamp<span className="text-[#D4A373]">.</span>YourHoliday<span className="text-[#D4A373]">.</span>PT
              </span>
            </div>
            <p className="text-white/60 font-manrope font-light text-sm leading-relaxed mb-5 max-w-sm">
              {t("footer.tagline")}
            </p>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-[#3A4D39]/40 rounded-full w-fit">
              <span className="w-2 h-2 rounded-full bg-[#D4A373]" />
              <span className="text-xs font-manrope text-[#D4A373] uppercase tracking-wider">
                {t("footer.sustainableLabel")}
              </span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-white/50 mb-5 font-manrope">
              {t("footer.quickLinks")}
            </p>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.key}>
                  <a
                    href={link.href}
                    className="text-sm text-white/70 hover:text-white transition-colors font-manrope"
                    data-testid={`footer-link-${link.key}`}
                  >
                    {t(`nav.${link.key}`)}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact & Legal */}
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-white/50 mb-5 font-manrope">
              {t("footer.contactTitle")}
            </p>
            <ul className="space-y-2.5 mb-7">
              <li>
                <a
                  href="mailto:welcome@yourholiday.pt"
                  className="text-sm text-white/70 hover:text-white transition-colors font-manrope"
                  data-testid="footer-email"
                >
                  welcome@yourholiday.pt
                </a>
              </li>
              <li className="text-sm text-white/70 font-manrope">+351 919 133 855</li>
              <li className="text-sm text-white/70 font-manrope">+351 919 133 750</li>
            </ul>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-white/50 mb-4 font-manrope">
              {t("footer.legalTitle")}
            </p>
            <ul className="space-y-2">
              <li>
                <a
                  href="https://book.yourholiday.pt/en/privacy/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-white/60 hover:text-white transition-colors font-manrope flex items-center gap-1"
                  data-testid="footer-privacy"
                >
                  {t("footer.privacy")} <ExternalLink className="w-3 h-3" />
                </a>
              </li>
              <li>
                <a
                  href="https://book.yourholiday.pt/en/booking-conditions/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-white/60 hover:text-white transition-colors font-manrope flex items-center gap-1"
                  data-testid="footer-terms"
                >
                  {t("footer.terms")} <ExternalLink className="w-3 h-3" />
                </a>
              </li>
              <li>
                <a
                  href="https://www.livroreclamacoes.pt/inicio"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-white/60 hover:text-white transition-colors font-manrope flex items-center gap-1"
                  data-testid="footer-complaints"
                >
                  {t("footer.complaints")} <ExternalLink className="w-3 h-3" />
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div>
            <p className="text-xs text-white/40 font-manrope">Glamp Your Holiday — Eco Resort</p>
            <p className="text-xs text-white/40 font-manrope mt-1">
              © {new Date().getFullYear()} All rights reserved.
            </p>
          </div>
          <a
            href="https://book.yourholiday.pt/en/search-availability/"
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-full px-6 py-2.5 bg-[#D4A373] text-white text-sm font-medium hover:bg-[#C29262] transition-colors font-manrope shrink-0"
            data-testid="footer-book-btn"
          >
            {t("footer.bookNow")}
          </a>
        </div>
      </div>
    </footer>
  );
}
